import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';

/**
 * DataRow — the "title + meta + trailing action" row pattern used by
 * Dashboard link rows, Pages list, Domains list, Members list,
 * AdminBotResearch table.
 *
 * Renders a glass card with primary hover-glow. The main clickable area
 * is either a router link (when `to` is passed) or just static content.
 * `trailing` is rendered OUTSIDE the link so trailing buttons (Duplicate,
 * Delete, etc.) don't trigger navigation.
 *
 * Composition pattern:
 *   <DataRow to={`/app/links/${id}`} trailing={<DuplicateBtn />}>
 *     <DataRow.Title>{title}</DataRow.Title>
 *     <DataRow.Meta>{shortCode} → {targetUrl}</DataRow.Meta>
 *   </DataRow>
 *
 * Title and Meta are exposed as static members for the import ergonomics.
 */
interface DataRowProps {
  /** When set, the main content area is a RouterLink. */
  to?: string;
  /** Right-side static content — number / badge / trailing action button. */
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function DataRow({ to, trailing, children, className = '' }: DataRowProps) {
  const card =
    'group flex flex-wrap items-stretch rounded-2xl border border-[var(--color-border)] bg-[var(--surface-glass)] backdrop-blur-xl transition hover:border-[var(--color-primary)]/40 hover:bg-[var(--surface-glass-strong)]';
  const content = (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 p-4">
      {children}
    </div>
  );
  if (to) {
    return (
      <li className={`${card} ${className}`}>
        <RouterLink to={to} className="flex min-w-0 flex-1 flex-wrap items-center gap-3 p-4">
          {children}
        </RouterLink>
        {trailing && (
          <div className="flex items-stretch border-l border-[var(--color-border)] opacity-60 transition group-hover:opacity-100">
            {trailing}
          </div>
        )}
      </li>
    );
  }
  return (
    <li className={`${card} ${className}`}>
      {content}
      {trailing && (
        <div className="flex items-stretch border-l border-[var(--color-border)]">
          {trailing}
        </div>
      )}
    </li>
  );
}

DataRow.Title = function Title({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2 truncate font-medium text-[var(--color-foreground)]">
      {children}
    </div>
  );
};

DataRow.Meta = function Meta({ children, mono = true }: { children: ReactNode; mono?: boolean }) {
  return (
    <div
      className={`mt-1 truncate text-xs text-[var(--color-on-surface-variant)] ${
        mono ? 'font-mono' : ''
      }`}
    >
      {children}
    </div>
  );
};

DataRow.Stack = function Stack({ children }: { children: ReactNode }) {
  return <div className="min-w-0 flex-1">{children}</div>;
};
